import {
  faCircleXmark,
  faLightbulb,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { faListCheck, faSitemap } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '~/helpers/yupResolver';
import classNames from 'classnames';
import { format, parse } from 'date-fns';
import { EditorState, RawDraftContentState, convertToRaw } from 'draft-js';
import { motion } from 'framer-motion';
import { nanoid } from 'nanoid';
import pluralize from 'pluralize';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaXmark } from 'react-icons/fa6';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation } from 'urql';
import * as yup from 'yup';
import { useAppContext } from '~/App';
import { Prompt } from '~/components/Prompt';
import { Input } from '~/components/form/TextField';
import { Confirm } from '~/components/ui/Confirm';
import { BULK_ASSET_JOB_CREATION_KEY } from '~/constants/bulkAssetOperations';
import { CreateJobScope, JobStatus, TaskInput } from '~/generated/graphql';
import { getFragmentData, graphql } from '~/gql';
import {
  CreateJobFormQuery,
  CreateJobInput,
  CreateJobsMutation,
  JobTemplateFieldsFragment,
} from '~/gql/graphql';
import { ContactFields } from '~/graphql/fragment/ContactFields';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { remove, splice, unique } from '~/helpers/array';
import { getBulkAssetTaskName } from '~/helpers/bulkAssetTaskName';
import {
  TemplateAutomationError,
  createTemplateAndAutomation,
} from '~/helpers/automationTemplateHelpers';
import { notEmpty } from '~/helpers/filter';
import { storeCreatedJobDates } from '~/helpers/jobCreationHints';
import { type JobConfig } from '~/helpers/templateHelpers';
import { getTenantTimezone } from '~/helpers/timezoneUtils';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useLocalStorage } from '~/hooks/useLocalStorage';
import { useOptionalActivityListContext } from '~/routes/activity';
import { useContacts } from '~/routes/resources/contacts';
import { Option } from '~/types';
import { SideLayout } from '../../layouts/side/SideLayout';
import { useAssets } from '../AssetSelect/AssetSelect';
import { FindAddContact } from '../FindAddContact';
import { FindAddSite } from '../FindAddSite';
import { useSites } from '../FindAddSite/FindAddSite';
import { TagCombobox, TagEntityType } from '../TagCombobox';
import { useTags } from '../TagCombobox/tags';
import { ActivityListItem_Activity } from '../activity/ActivityListItem';
import { AttributeSelect } from '../form/AttributeSelect';
import { TimePicker } from '../form/TimePicker';
import { Editor } from '../form/editor/Editor';
import { Accordion } from '../ui/Accordion';
import { HelperPopup } from '../ui/HelperPopup';
import { JobsRepeat, RepeatValue, repeatToInput } from '../ui/JobsRepeat';
import { Label } from '../ui/Label';
import { Switch } from '../ui/nucleus/Switch';
import { Kind, toAttachmentInput } from '../upload2';
import { ControlledUpload, UploadResult } from '../upload2/Upload';
import {
  AssetDrivenAssetsSection,
  AssetDrivenJobPreview,
  calculateTaskPreviews,
  generateTasksFromAssetDrivenTemplate,
} from './AssetDrivenJobPreview';
import {
  AssetDrivenBadge,
  getTemplateBehavior,
  isAssetDrivenTemplate,
} from './AssetDrivenTemplateIndicator';
import { ManageTasks, UseTemplate } from './CreateJobForm/ManageTasks';
import { CreateTemplateButton } from './CreateTemplateButton';
import { CustomTaskForm } from './CustomTaskForm';
import { useJob } from './JobView';
import { SaveJobActions, SubmitOptions } from './SaveJobActions';
import { TemplateSwitchWarning } from './TemplateSwitchWarning';
import { getDefaultScheduledEndTime } from './getDefaultScheduledEndTime';
import { useTemplateManager } from './hooks/useTemplateManager';

/**
 * State for asset-driven template workflow
 */
type AssetDrivenTemplateState = {
  /** Whether an asset-driven template is currently selected */
  isAssetDriven: boolean;
  /** Selected assets for task generation */
  selectedAssetIds: string[];
};

export type LocationOptions = {
  Asset: Option[];
  Contact: Option[];
  Site: Option[];
  StockTransfer: Option[];
  Elsewhere: Option[];
};

const CreateJobsMutationDocument = graphql(`
  mutation CreateJobs($input: CreateJobInput!) {
    createJobs(input: $input) {
      id
      status
      name
      createdAt
      location {
        id
      }
      scheduledStartDate
      scheduledEndDate
    }
  }
`);

const CreateJobTemplateDocument = graphql(`
  mutation CreateJobTemplate($input: JobTemplateInput!) {
    createJobTemplate(input: $input) {
      id
    }
  }
`);

const CreateAutomationDocument = graphql(`
  mutation CreateAutomation($input: AutomationInput!) {
    createAutomation(input: $input)
  }
`);

const CreateJobSchema = yup.object().shape({
  name: yup.string().required('Job Name is required'),
  scopeIds: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one Place is required'),
  repeat: yup.object().shape({
    isRepeat: yup.boolean(),
    numOfRepeats: yup.string().when('isRepeat', {
      is: true,
      then: (schema) => schema.required('Number of repeats is required'),
    }),
  }),
});

/**
 * Form values that differ from the input type
 */
type DivergentFormValues = {
  guestyCleaningStatus: boolean;
  repeat: RepeatValue;
  // The upload field value held by the form differs from the input type
  attachments: UploadResult[];
  // Notes use rich text editor with RawDraftContentState
  notes: RawDraftContentState | null;
};
type FormValues = Omit<CreateJobInput, keyof DivergentFormValues> &
  DivergentFormValues;

/**
 * FIXME these props are only required by popup on site view, not the create jobs form which is actually a route component (/jobs/new)
 * These could be moved out using composition to avoid a swathe of conditional logic
 * We want to display the same form in a different context
 * @deprecated use Props instead
 */
type CreateJobProps = {
  /** @deprecated see FIXME above */
  onSuccess?: (data: CreateJobsMutation) => void;
};

type Props = {
  data: CreateJobFormQuery;
  /** If provided, set initial value and lock the control to this location */
  locationId?: string;
  /** Passed through to SideLayout.Head */
  onClose: () => void;
  /** Animation source to determine transition type */
  animationSource?: 'asset' | 'job' | 'default';
  /** Asset data for pre-population when opened from asset view */
  initialAsset?: {
    id: string;
    name: string;
    type: string;
    deployment?: {
      location?: { id: string; name: string };
      space?: { id: string; name: string };
    };
  };
  /** When true, render footer outside of SideLayout (popup pattern) */
  isPopup?: boolean;
};

/**
 * Checks if a name looks like an asset code (e.g., "ABC0012", "XYZ123")
 */
const isAssetCode = (name: string): boolean => {
  // Pattern: starts with letters, followed by numbers, minimal or no spaces
  // Matches patterns like "ABC0012", "XYZ123", "A1B2C3"
  return /^[A-Za-z]+\d+[A-Za-z0-9]*$/.test(name.trim());
};

/**
 * Generates a job name from asset names for bulk asset job creation.
 * If all assets have the same base name (e.g., "smoke alarm 1", "smoke alarm 2"),
 * returns the plural form. Otherwise, returns a comma-separated list.
 * Asset codes are excluded from grouping logic.
 */
const generateBulkAssetJobName = (
  assetNames: string[],
  jobType?: 'service' | 'clean' | 'job'
): string => {
  if (assetNames.length === 0) {
    return '';
  }

  if (assetNames.length === 1) {
    return assetNames[0];
  }

  // Separate asset codes from named assets
  const assetCodes = assetNames.filter(isAssetCode);
  const namedAssets = assetNames.filter((name) => !isAssetCode(name));

  // Extract base names by removing trailing numbers, patterns like "4-seater", and whitespace
  // e.g., "smoke alarm 1" -> "smoke alarm", "Fridge 2" -> "Fridge", "Dining Set 4-seater" -> "Dining Set"
  const extractBaseName = (name: string): string => {
    // Remove trailing patterns like "4-seater", "6-seater", etc. (number followed by hyphen and word)
    let baseName = name.replace(/\s+\d+-\w+$/, '').trim();
    // Remove trailing numbers with whitespace (e.g., "smoke alarm 1" -> "smoke alarm")
    baseName = baseName.replace(/\s+\d+$/, '').trim();
    return baseName;
  };

  // Process named assets
  let namedAssetsDisplay = '';
  if (namedAssets.length > 0) {
    const baseNames = namedAssets.map(extractBaseName);
    const uniqueBaseNames = [
      ...new Set(baseNames.map((name) => name.toLowerCase())),
    ];

    // If all named assets share the same base name AND there are multiple assets, pluralize it
    if (uniqueBaseNames.length === 1 && namedAssets.length > 1) {
      const baseName = baseNames[0];
      // Use pluralize library for proper pluralization (handles irregular plurals, etc.)
      const pluralName = pluralize(baseName);
      // Capitalize first letter of each word for better formatting (title case)
      namedAssetsDisplay = pluralName
        .split(' ')
        .map((word) => {
          // Preserve existing capitalization if already capitalized, otherwise capitalize
          if (word.length > 0) {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          }
          return word;
        })
        .join(' ');
    } else {
      // Different base names or only one asset - use comma-separated list of full names
      namedAssetsDisplay = namedAssets.join(', ');
    }
  }

  // Combine named assets and codes
  const parts: string[] = [];
  if (namedAssetsDisplay) {
    parts.push(namedAssetsDisplay);
  }
  if (assetCodes.length > 0) {
    parts.push(...assetCodes);
  }

  return parts.join(', ');
};

/**
 * Generates a better job note for asset-driven templates.
 * Uses asset names to create a more descriptive note than the generic "Asset maintenance job for X assets".
 */
const generateAssetDrivenTemplateNote = (
  assetNames: string[],
  templateName?: string,
  jobType?: 'service' | 'clean' | 'job'
): string => {
  if (assetNames.length === 0) {
    return '';
  }

  // Generate asset name display using the same logic as job name generation
  const assetNameDisplay = generateBulkAssetJobName(assetNames, jobType);

  // Determine the action verb based on job type
  let actionVerb = 'Maintenance';
  switch (jobType) {
    case 'service':
      actionVerb = 'Service';
      break;
    case 'clean':
      actionVerb = 'Clean';
      break;
    case 'job':
    default:
      actionVerb = 'Maintenance';
      break;
  }

  // Build the note with template name if available
  if (templateName) {
    // Clean template name (remove standalone "template" word and adjacent separators)
    const cleanTemplateName = templateName
      .replace(/[-_\s]*\btemplate\b[-_\s]*/gi, ' ')
      .trim();

    return `${cleanTemplateName} - ${actionVerb} job for ${assetNameDisplay}`;
  }

  // No template name - use asset names directly
  return `${actionVerb} job for ${assetNameDisplay}`;
};

const informMultipleSitesSelected = (jobCount: number) => {
  toast.info(`Jobs will be created for ${jobCount} places.`, {
    theme: 'dark',
    className: 'bg-[#0D9F8F] text-white',
    hideProgressBar: true,
    icon: <FontAwesomeIcon icon={faLightbulb} />,
  });
};

export const CreateJobForm = ({
  data,
  onClose,
  onSuccess: popupOnSuccess,
  animationSource,
  initialAsset,
  isPopup = false,
  ...props
}: Props & CreateJobProps) => {
  const { ability, tenant } = useAppContext();
  const navigate = useNavigate();
  const { t } = useTranslation('job');

  const activityContext = useOptionalActivityListContext();

  const [searchParams] = useSearchParams();
  const activitySearchId = searchParams.get('activity');
  const activitySearchIds = activitySearchId?.split(',');
  const urlSiteId = searchParams.get('siteId');
  const urlDate = searchParams.get('date');
  const urlTime = searchParams.get('time');

  // Check for bulk asset job creation data from sessionStorage
  // This is set when user selects multiple assets and clicks "+ Job" from bulk selection UI
  // Store in ref to persist across renders (sessionStorage will be cleared after tasks are initialized)
  const bulkAssetJobCreationRef = useRef<{
    assetIds: string[];
    jobType?: 'service' | 'clean' | 'job';
    templateId?: string; // Optional template ID for pre-selecting a template
    assets: Array<{
      id: string;
      name: string;
      type?: string;
      deploymentLocationId?: string;
      deploymentLocationName?: string;
      deploymentSpaceName?: string;
    }>;
  } | null>(null);

  const bulkAssetJobCreation = useMemo(() => {
    // If already read, use the ref
    if (bulkAssetJobCreationRef.current) {
      return bulkAssetJobCreationRef.current;
    }

    try {
      const storedData = sessionStorage.getItem(BULK_ASSET_JOB_CREATION_KEY);
      if (storedData) {
        const parsed = JSON.parse(storedData) as {
          assetIds: string[];
          jobType?: 'service' | 'clean' | 'job';
          templateId?: string;
          assets: Array<{
            id: string;
            name: string;
            type?: string;
            deploymentLocationId?: string;
            deploymentLocationName?: string;
            deploymentSpaceName?: string;
          }>;
        };
        // Store in ref to persist across renders AND across Strict Mode remounts
        bulkAssetJobCreationRef.current = parsed;
        // NOTE: Do NOT clear sessionStorage here - React Strict Mode will remount
        // and the data will be lost before the second mount can read from the ref.
        // The ref check at the top handles subsequent reads within the same session.

        return parsed;
      }
    } catch (e) {
      console.error('Error reading bulk asset job creation data:', e);
    }
    return null;
  }, []);

  // Get unique deployment locations from bulk assets for pre-populating scopeIds
  const bulkAssetScopeIds = useMemo(() => {
    if (!bulkAssetJobCreation) return [];
    const locationIds = bulkAssetJobCreation.assets
      .map((a) => a.deploymentLocationId)
      .filter((id): id is string => !!id);
    return [...new Set(locationIds)];
  }, [bulkAssetJobCreation]);
  const unmaskedActivities = getFragmentData(
    ActivityListItem_Activity,
    (activityContext?.activities ?? []).reduce<any[]>((acc, item) => {
      if (typeof item !== 'string' && (item as any)?.node) {
        acc.push((item as any).node);
      }
      return acc;
    }, [])
  );

  /** Are we creating a new job from within another job? */
  const createFrom = useJob({ required: false });
  const createFromActivity = unmaskedActivities
    ?.filter((activity) => activitySearchIds?.includes(activity?.id))
    .sort((a, b) => (new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1));
  const createFromActivityMessage = createFromActivity
    ?.map((act) => act?.comment?.body || act?.task?.notes)
    .filter(Boolean)
    .join('\n\n');

  const createFromActivityAttachments = (): UploadResult[] | null => {
    const attachments = createFromActivity
      ?.map((att) => att?.task?.attachments || att?.comment?.attachments)
      .filter(notEmpty)
      .flat()
      .map((att) => ({
        id: att.id,
        kind: att.kind as Kind,
        name: att.originalFilename,
        preview: att.thumb,
      }));

    return attachments && attachments.length > 0 ? attachments : null;
  };

  const [, createJobs] = useMutation(CreateJobsMutationDocument);
  const [, createJobTemplate] = useMutation(CreateJobTemplateDocument);
  const [, createAutomation] = useMutation(CreateAutomationDocument);
  const [sites] = useSites();
  const [allTags, reload] = useTags(TagEntityType.Job);
  const { isMobile } = useBreakpoint();
  const [allAssets] = useAssets();

  const [navigateTo, setNavigateTo] = useState<string>();
  useEffect(() => {
    if (navigateTo) navigate(navigateTo);
  }, [navigateTo, navigate]);

  const { taskState, showTask, setShowTask, updateTask, deleteTask } =
    useEditTasks();
  // Extract setTasks for stable reference in callbacks
  const [, setTasks] = taskState;

  // Track if we've already initialized tasks for this asset to prevent re-initialization
  const tasksInitializedRef = useRef<string | null>(null);
  // Track if we've pre-populated asset selection for asset-driven templates
  const assetSelectionInitializedRef = useRef(false);

  const contacts = useContacts();
  const { pathname } = useLocation();
  const pageName = pathname.split('/')[1];

  // unmask fragment data
  const dataContacts = getFragmentData(ContactFields, data.contacts);
  const currentUserContact = dataContacts.find(
    (contact) => contact.isCurrentUser
  );

  // Create separate function for asset-based job creation
  // Memoized to prevent recreation on every render which causes infinite loops
  const createFromAsset = useMemo(() => {
    if (!initialAsset) return null;

    const taskId = nanoid();
    // Create default description in the same format as ManageTasks
    const editorState = EditorState.createEmpty();
    const defaultDescription = convertToRaw(editorState.getCurrentContent());

    return {
      scopeIds: initialAsset.deployment?.location?.id
        ? [initialAsset.deployment.location.id]
        : [],
      tasks: [
        {
          id: taskId,
          type: 'Asset',
          name: `${initialAsset.name} Maintenance`,
          description: JSON.stringify(defaultDescription),
          attributes: [],
          asset: {
            selection: 'specific',
            assets: [String(initialAsset.id)],
            spaces: initialAsset.deployment?.space?.name
              ? [initialAsset.deployment.space.name]
              : [],
            assetTypes: [initialAsset.type],
            deploy: 'Job',
            allow: ['edit'],
          },
          _destroy: false,
        },
      ],
    };
  }, [initialAsset]);

  // Compute initial scope IDs with proper precedence
  // Wrapped in useCallback to provide stable reference for useMemo dependencies
  const getInitialScopeIds = useCallback(() => {
    if (createFrom?.job?.location) {
      return [createFrom.job.location.id];
    }

    // When creating from asset, prioritize props.locationId or asset's location
    // This ensures asset context always takes precedence over activities
    if (initialAsset) {
      // First try asset's deployment location
      if (createFromAsset?.scopeIds?.length) {
        return createFromAsset.scopeIds;
      }
      // Fall back to locationId prop passed from asset route
      if (props.locationId) {
        return [props.locationId];
      }
      // If asset exists but has no location, return empty to force user selection
      return [];
    }

    // Bulk asset job creation - use deployment locations from selected assets
    if (bulkAssetScopeIds.length > 0) {
      return bulkAssetScopeIds;
    }

    // Activity-based job creation (only if not from asset)
    if (createFromActivity && createFromActivity.length > 0) {
      const activityScopeIds = unique(
        createFromActivity.map((act) => act.job?.site?.id || '').filter(Boolean)
      );
      return activityScopeIds;
    }

    // URL siteId parameter (from calendar hover dialog)
    if (urlSiteId) {
      return [urlSiteId];
    }

    if (props.locationId) {
      return [props.locationId];
    }

    return [];
  }, [
    createFrom?.job?.location,
    initialAsset,
    createFromAsset?.scopeIds,
    bulkAssetScopeIds,
    createFromActivity,
    urlSiteId,
    props.locationId,
  ]);

  // Compute initial asset tasks from createFromAsset or bulkAssetJobCreation
  // Wrapped in useCallback to provide stable reference for useMemo dependencies
  const getInitialAssetTasks = useCallback(() => {
    // Single asset creation
    if (createFromAsset?.tasks) {
      return createFromAsset.tasks;
    }

    // If templateId is preselected, let the template control task generation
    if (bulkAssetJobCreation?.templateId) {
      return [];
    }

    // Bulk asset creation - create one asset task per selected asset
    if (bulkAssetJobCreation?.assets?.length) {
      const editorState = EditorState.createEmpty();
      const defaultDescription = convertToRaw(editorState.getCurrentContent());

      const taskNameSuffix = getBulkAssetTaskName(bulkAssetJobCreation.jobType);

      return bulkAssetJobCreation.assets.map((asset) => ({
        id: nanoid(),
        type: 'Asset',
        name: `${asset.name} ${taskNameSuffix}`,
        description: JSON.stringify(defaultDescription),
        attributes: [],
        asset: {
          selection: 'specific',
          assets: [String(asset.id)],
          spaces: asset.deploymentSpaceName ? [asset.deploymentSpaceName] : [],
          assetTypes: asset.type ? [asset.type] : [],
          deploy: 'Job',
          allow: ['edit'],
        },
        _destroy: false,
      }));
    }

    return [];
  }, [
    createFromAsset,
    bulkAssetJobCreation?.assets,
    bulkAssetJobCreation?.templateId,
  ]); // Fixed: createFromAsset is now memoized

  // Compute values once and use them directly - memoize to prevent recalculation
  const initialScopeIds = useMemo(
    () => getInitialScopeIds(),
    [getInitialScopeIds]
  );

  const initialAssetTasks = useMemo(
    () => getInitialAssetTasks(),
    [getInitialAssetTasks]
  );

  // Helper to create RawDraftContentState from plain text
  const createNotesContent = useCallback(
    (text: string | null | undefined): RawDraftContentState => {
      if (!text) {
        // Return empty editor state
        const editorState = EditorState.createEmpty();
        return convertToRaw(editorState.getCurrentContent());
      }
      // Create a simple RawDraftContentState with the text as a single paragraph
      return {
        blocks: [
          {
            key: nanoid(5),
            text: text,
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {},
          },
        ],
        entityMap: {},
      };
    },
    []
  );

  // Show notification if bulk asset job creation spans multiple properties
  useEffect(() => {
    if (bulkAssetScopeIds.length > 1) {
      informMultipleSitesSelected(bulkAssetScopeIds.length);
    }
  }, [bulkAssetScopeIds]);

  const initialValues: FormValues = useMemo(
    () => ({
      // Pre-populate job name when creating from asset or bulk assets
      name: initialAsset
        ? `${initialAsset.name} Maintenance`
        : bulkAssetJobCreation
        ? (() => {
            const assetNames = bulkAssetJobCreation.assets.map((a) => a.name);
            const assetNameDisplay = generateBulkAssetJobName(
              assetNames,
              bulkAssetJobCreation.jobType
            );

            // Apply job type naming convention - action comes first, then asset names
            switch (bulkAssetJobCreation.jobType) {
              case 'service':
                return assetNameDisplay
                  ? `Service ${assetNameDisplay}`
                  : 'Service';
              case 'clean':
                return assetNameDisplay ? `Clean ${assetNameDisplay}` : 'Clean';
              case 'job':
              default:
                return assetNameDisplay
                  ? `Maintenance ${assetNameDisplay}`
                  : 'Asset Maintenance';
            }
          })()
        : '',
      scope: CreateJobScope.Site,
      scopeIds: initialScopeIds, // Use computed values directly
      fromTemplate: '',
      timeZone: getTenantTimezone(tenant),
      scheduledStartStrict: '1',
      scheduledStartDate: urlDate || '',
      scheduledStartTime: urlTime || '',
      scheduledEndStrict: '0',
      scheduledEndDate: '',
      scheduledEndTime: '',
      owner: currentUserContact ? [currentUserContact.id] : null,
      assignee: null,
      customer: null,
      enforceOrder: false,
      // Pre-populate tasks with asset task when creating from asset
      tasks: initialAssetTasks, // Use computed values directly
      includeAttributes: null,
      jobAttributes: null,
      tags: [],
      notes: createNotesContent(
        initialAsset
          ? `Maintenance job for ${initialAsset.name} (${initialAsset.type})`
          : bulkAssetJobCreation
          ? (() => {
              // Resolve the template and check if it's truly asset-driven
              let isTemplateAssetDriven = false;
              let templateName: string | undefined;

              if (bulkAssetJobCreation.templateId) {
                const templates = getFragmentData(
                  JobTemplateFields,
                  data.jobTemplates
                );
                const template = templates.find(
                  (t) => t.id === bulkAssetJobCreation.templateId
                );

                if (template) {
                  const templateBehavior = getTemplateBehavior(template);
                  isTemplateAssetDriven =
                    isAssetDrivenTemplate(templateBehavior);
                  templateName = template.name;
                }
              }

              if (isTemplateAssetDriven) {
                // Generate better notes for asset-driven templates using asset names
                const assetNames = bulkAssetJobCreation.assets.map(
                  (a) => a.name
                );

                return generateAssetDrivenTemplateNote(
                  assetNames,
                  templateName,
                  bulkAssetJobCreation.jobType
                );
              }

              // Fallback to generic notes for non-template bulk asset creation
              // Apply job type naming convention for notes
              switch (bulkAssetJobCreation.jobType) {
                case 'service':
                  return `Service job for ${
                    bulkAssetJobCreation.assets.length
                  } asset${bulkAssetJobCreation.assets.length > 1 ? 's' : ''}`;
                case 'clean':
                  return `Clean job for ${
                    bulkAssetJobCreation.assets.length
                  } asset${bulkAssetJobCreation.assets.length > 1 ? 's' : ''}`;
                case 'job':
                default:
                  return `Asset maintenance job for ${
                    bulkAssetJobCreation.assets.length
                  } asset${bulkAssetJobCreation.assets.length > 1 ? 's' : ''}`;
              }
            })()
          : createFromActivityMessage
      ),
      attachments: createFromActivityAttachments() ?? [],
      guestyCleaningStatus: false,
      repeat: {
        isRepeat: false,
        interval: '1',
        numOfRepeats: '',
        frequency: 'daily',
        repeatOnWeek: [],
        repeatOnMonth: '',
        repeatOnYear: '',
      },
      createRepeatAutomation: false,
      automationId: null,
    }),
    [
      initialAsset,
      initialScopeIds,
      urlDate,
      urlTime,
      currentUserContact?.id,
      initialAssetTasks,
      createFromActivityMessage,
      createNotesContent,
      tenant,
      bulkAssetJobCreation,
      data,
    ]
  );

  // Custom loading state to make form submission busy up until the redirection is completed
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const form = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(CreateJobSchema),
    resetOptions: { keepDirtyValues: true },
  });
  const {
    formState: { isDirty, errors },
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
  } = form;

  // Watch job name for real-time updates to desktop draft job pill
  const jobName = watch('name');
  const storage = useLocalStorage();

  // Get storage key for current job creation cell
  const getJobNameStorageKey = useCallback(() => {
    if (urlSiteId && urlDate) {
      return `draft.job.${urlSiteId}:${urlDate}`;
    }
    return null;
  }, [urlSiteId, urlDate]);

  // Save job name to localStorage when user types
  useEffect(() => {
    const storageKey = getJobNameStorageKey();
    if (storageKey && jobName) {
      storage.set(storageKey, jobName);

      // Dispatch event to notify other components (like the pill) about the change
      window.dispatchEvent(
        new CustomEvent('jobNameStorageChanged', {
          detail: { key: storageKey, value: jobName },
        })
      );
    }
  }, [jobName, storage, getJobNameStorageKey]);

  // Listen for localStorage changes from other devices/tabs
  useEffect(() => {
    const storageKey = getJobNameStorageKey();
    if (!storageKey) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue !== jobName) {
        setValue('name', e.newValue || '');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [getJobNameStorageKey, jobName, setValue]);

  const {
    applyTemplate,
    selectedTemplate,
    isTemplateModified,
    removeTemplate,
    resetInitialValues,
  } = useTemplateManager({
    form,
    taskState,
    isEditMode: false,
  });

  // Asset-driven template state management
  const [openAddAssetsDialog, setOpenAddAssetsDialog] = useState<
    (() => void) | null
  >(null);
  const [assetDrivenState, setAssetDrivenState] =
    useState<AssetDrivenTemplateState>({
      isAssetDriven: false,
      selectedAssetIds: [],
    });

  // Track if template switch warning should be shown
  const [showTemplateSwitchWarning, setShowTemplateSwitchWarning] =
    useState(false);
  const [pendingTemplate, setPendingTemplate] =
    useState<JobTemplateFieldsFragment | null>(null);

  /**
   * Apply template with behavior-specific handling
   */
  const applyTemplateWithBehavior = useCallback(
    (
      template: JobTemplateFieldsFragment,
      isAssetDriven: boolean,
      options?: { forceTemplateName?: boolean }
    ) => {
      if (isAssetDriven) {
        // For asset-driven templates, update state but don't apply tasks yet
        setAssetDrivenState({
          isAssetDriven: true,
          selectedAssetIds: [],
        });

        // Apply template metadata (name, tags, etc.) but not tasks
        // Explicitly skip tasks - they will be generated by applyAssetDrivenTemplateTasks
        // when assets are selected. Note: applyTemplate also checks isAssetDriven internally,
        // but passing skipTasks: true makes the intent explicit and provides defense-in-depth.
        applyTemplate(template, { ...options, skipTasks: true });
      } else {
        // For static templates, apply normally (including tasks)
        setAssetDrivenState({
          isAssetDriven: false,
          selectedAssetIds: [],
        });
        applyTemplate(template, options);
      }
    },
    [applyTemplate]
  );

  /**
   * Handle template selection with asset-driven awareness
   * For asset-driven templates, we don't apply tasks immediately -
   * instead we surface the asset selection UI first
   */
  const handleTemplateSelect = useCallback(
    (template: JobTemplateFieldsFragment) => {
      const templateBehavior = getTemplateBehavior(template);
      const isNewTemplateAssetDriven = isAssetDrivenTemplate(templateBehavior);
      const hasExistingTasks =
        taskState[0].filter((t) => !t._destroy).length > 0;
      const hasSelectedAssets = assetDrivenState.selectedAssetIds.length > 0;

      // Check if we need to warn about template switching
      const shouldWarn =
        (selectedTemplate && hasExistingTasks) ||
        (assetDrivenState.isAssetDriven && hasSelectedAssets);

      if (shouldWarn) {
        // Store pending template and show warning
        setPendingTemplate(template);
        setShowTemplateSwitchWarning(true);
        return;
      }

      // Apply template directly
      applyTemplateWithBehavior(template, isNewTemplateAssetDriven);
    },
    [
      selectedTemplate,
      taskState,
      assetDrivenState.isAssetDriven,
      assetDrivenState.selectedAssetIds,
      applyTemplateWithBehavior,
    ]
  );

  /**
   * Confirm template switch after warning
   */
  const handleConfirmTemplateSwitch = useCallback(() => {
    if (!pendingTemplate) return;

    const templateBehavior = getTemplateBehavior(pendingTemplate);
    const isNewTemplateAssetDriven = isAssetDrivenTemplate(templateBehavior);

    // Clear existing state
    removeTemplate();
    setAssetDrivenState({
      isAssetDriven: false,
      selectedAssetIds: [],
    });

    // Apply the new template
    applyTemplateWithBehavior(pendingTemplate, isNewTemplateAssetDriven);

    // Clean up
    setPendingTemplate(null);
    setShowTemplateSwitchWarning(false);
  }, [pendingTemplate, removeTemplate, applyTemplateWithBehavior]);

  /**
   * Cancel template switch
   */
  const handleCancelTemplateSwitch = useCallback(() => {
    setPendingTemplate(null);
    setShowTemplateSwitchWarning(false);
  }, []);

  /**
   * Update selected assets for asset-driven template
   */
  const handleAssetSelectionChange = useCallback((assetIds: string[]) => {
    setAssetDrivenState((prev) => ({
      ...prev,
      selectedAssetIds: assetIds,
    }));
  }, []);

  const applyAssetDrivenTemplateTasks = useCallback(
    (template: JobTemplateFieldsFragment, assetIds: string[]) => {
      if (!template) {
        return;
      }

      const selectedAssets = allAssets.filter((asset) =>
        assetIds.includes(asset.id)
      );

      const generatedTasks = generateTasksFromAssetDrivenTemplate(
        template,
        selectedAssets
      );

      // Compute new tasks array once to avoid stale state
      // Use getValues to get current form value as source of truth
      const currentTasks = getValues('tasks') || [];
      const activeTasks = currentTasks.filter((task) => !task._destroy);

      // Filter out template-generated tasks
      const nonTemplateTasks = activeTasks.filter(
        (task) =>
          !(task as TaskInput & { templateTaskId?: string }).templateTaskId
      );

      // Filter out manually added Asset tasks that reference removed assets
      // For Asset tasks, remove the task if any referenced asset is no longer selected
      const selectedAssetIdSet = new Set(assetIds);
      const validNonTemplateTasks = nonTemplateTasks.filter((task) => {
        // Only filter Asset-type tasks
        if (task.type !== 'Asset' || !task.asset?.assets) {
          return true; // Keep non-Asset tasks and Asset tasks without specific assets
        }

        // For Asset tasks with specific assets, check if all referenced assets are still selected
        // If any asset was removed, remove the entire task
        const taskAssetIds = task.asset.assets;
        const allAssetsStillSelected = taskAssetIds.every((assetId) =>
          selectedAssetIdSet.has(assetId)
        );

        return allAssetsStillSelected;
      });

      const newTasks = [...validNonTemplateTasks, ...generatedTasks];

      // Update both state and form value with the same computed array to keep them in sync
      setTasks(newTasks);
      setValue('tasks', newTasks);
    },
    [allAssets, setValue, getValues, setTasks] // Fixed: use setTasks instead of entire taskState array to prevent infinite loops
  );
  /**
   * CRITICAL RACE CONDITION FIX - DO NOT MODIFY WITHOUT UNDERSTANDING
   *
   * This effect regenerates tasks when asset selection changes in asset-driven templates.
   *
   * THE PROBLEM (that caused tasks to blink in/out):
   * 1. User clicks to open a task → handleShowTask sets openingTaskRef and showTask
   * 2. If assets change simultaneously, this effect runs and closes the task with setShowTask(null)
   * 3. The task opens briefly then immediately closes → creates "blinking" effect
   * 4. This happened repeatedly because asset selection can trigger during task opening
   * 5. Additionally, the effect was running on every render due to allAssets reference changes
   *
   * THE FIX:
   * - Check openingTaskRef BEFORE closing the task
   * - If a task is currently being opened, DO NOT close it
   * - This prevents the race condition where we close a task that's in the process of opening
   * - The openingTaskRef is managed by effects below and cleared after 300ms safety window
   * - Use a ref to track previous asset IDs to prevent running when IDs haven't actually changed
   *
   * WHY THIS WORKS:
   * - When user clicks a task, openingTaskRef is set BEFORE showTask changes
   * - This effect sees the ref and skips closing, allowing the task to fully open
   * - After the task is open and stable, the ref is cleared
   * - Future asset changes can then properly close/regenerate tasks
   * - The ref comparison prevents running on every render when allAssets reference changes
   *
   * DEPENDENCIES EXPLAINED:
   * - showTask is INTENTIONALLY EXCLUDED to prevent infinite loops
   * - We only want to regenerate when assets/template change, not when task opens/closes
   * - The openingTaskRef check handles the synchronization without needing showTask as a dependency
   * - We use a ref to track previous asset IDs to prevent running on array reference changes
   */
  // Track previous asset IDs to prevent unnecessary effect runs
  const prevAssetIdsRef = useRef<string>('');

  useEffect(() => {
    if (assetDrivenState.isAssetDriven && selectedTemplate) {
      // Create a stable string representation of current asset IDs
      // Create a non-mutating copy before sorting to avoid mutating state
      const currentAssetIdsKey = [...assetDrivenState.selectedAssetIds]
        .sort()
        .join(',');

      // Only regenerate if asset IDs actually changed
      if (prevAssetIdsRef.current === currentAssetIdsKey) {
        return;
      }

      prevAssetIdsRef.current = currentAssetIdsKey;

      // CRITICAL: Check if a task is currently being opened
      // If openingTaskRef is set, DO NOT close the task - this prevents blinking
      if (showTask && !openingTaskRef.current) {
        // Only close if we're NOT in the middle of opening a task
        setShowTask(null);
      }

      // Regenerate tasks based on selected assets
      applyAssetDrivenTemplateTasks(
        selectedTemplate,
        assetDrivenState.selectedAssetIds
      );
    } else {
      // Reset tracking when not in asset-driven mode
      prevAssetIdsRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assetDrivenState.isAssetDriven,
    assetDrivenState.selectedAssetIds, // Keep this to trigger the effect, but use ref to prevent unnecessary runs
    selectedTemplate,
    applyAssetDrivenTemplateTasks,
    // Intentionally exclude showTask and setShowTask from dependencies
    // to prevent infinite loops - we only want to close the task when assets change
    // openingTaskRef is also excluded because it's a ref (doesn't trigger re-renders)
  ]);

  // Get templates early for auto-apply logic
  const templates = getFragmentData(JobTemplateFields, data.jobTemplates);

  // Initialize the form and template tracking just once
  useEffect(() => {
    // Set a timeout to ensure form is fully initialized
    const timer = setTimeout(() => {
      resetInitialValues();
      // Clear sessionStorage AFTER form is initialized to prevent Strict Mode issues
      // The ref preserves the data for the session, so we can safely clear sessionStorage now
      sessionStorage.removeItem(BULK_ASSET_JOB_CREATION_KEY);
    }, 0);

    return () => clearTimeout(timer);
    // Run once on mount - resetInitialValues is stable and doesn't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply template if templateId is provided in bulkAssetJobCreation
  useEffect(() => {
    if (
      bulkAssetJobCreation?.templateId &&
      !selectedTemplate &&
      templates.length > 0
    ) {
      const templateToApply = templates.find(
        (t) => t.id === bulkAssetJobCreation.templateId
      );
      if (templateToApply) {
        const templateBehavior = getTemplateBehavior(templateToApply);
        const isNewTemplateAssetDriven =
          isAssetDrivenTemplate(templateBehavior);
        // Force template name for bulk asset job creation with templates
        applyTemplateWithBehavior(templateToApply, isNewTemplateAssetDriven, {
          forceTemplateName: true,
        });

        // CRITICAL FIX: For asset-driven templates with bulk assets, we need to
        // also set the selectedAssetIds from the bulk assets so that the
        // applyAssetDrivenTemplateTasks effect will trigger and generate tasks.
        // Without this, the template is applied but no tasks are generated because
        // applyTemplateWithBehavior sets selectedAssetIds to [] for asset-driven templates.
        if (isNewTemplateAssetDriven && bulkAssetJobCreation.assets?.length) {
          const bulkAssetIds = bulkAssetJobCreation.assets.map((a) => a.id);
          setAssetDrivenState({
            isAssetDriven: true,
            selectedAssetIds: bulkAssetIds,
          });
        }
      }
    }
  }, [
    bulkAssetJobCreation?.templateId,
    bulkAssetJobCreation?.assets,
    selectedTemplate,
    templates,
    applyTemplateWithBehavior,
  ]);

  // Simplified effect for initializing tasks - FIX INFINITE LOOP
  useEffect(() => {
    if (
      initialAsset &&
      tasksInitializedRef.current !== initialAsset.id &&
      taskState[0].length === 0
    ) {
      const [, setTasks] = taskState;
      const assetTasks = getInitialAssetTasks(); // Get fresh tasks
      setTasks(assetTasks);
      // Also update the form tasks field
      setValue('tasks', assetTasks);

      // Mark this asset as initialized
      tasksInitializedRef.current = initialAsset.id;
    }
  }, [initialAsset?.id, setValue, taskState[0].length, getInitialAssetTasks]); // Fixed: depend on taskState[0].length instead of taskState[0] to prevent infinite loop

  // Initialize tasks for bulk asset job creation
  useEffect(() => {
    if (
      bulkAssetJobCreation &&
      !initialAsset && // Only run if not single asset creation
      tasksInitializedRef.current !== 'bulk-assets' &&
      taskState[0].length === 0
    ) {
      const [, setTasks] = taskState;
      const assetTasks = getInitialAssetTasks(); // Get fresh tasks
      if (assetTasks.length > 0) {
        setTasks(assetTasks);
        // Also update the form tasks field
        setValue('tasks', assetTasks);
        // Mark bulk assets as initialized
        tasksInitializedRef.current = 'bulk-assets';
        // Note: sessionStorage is already cleared in bulkAssetJobCreation useMemo after parsing
      }
    }
  }, [
    bulkAssetJobCreation,
    initialAsset,
    setValue,
    taskState[0].length, // Fixed: depend on taskState[0].length instead of taskState[0] to prevent infinite loop
    getInitialAssetTasks,
  ]);

  // Track last activitySearchId to prevent unnecessary resets
  const lastActivitySearchIdRef = useRef<string | null>(null);

  // Handle activity search ID changes separately
  useEffect(() => {
    if (
      activitySearchId &&
      activitySearchId !== lastActivitySearchIdRef.current
    ) {
      lastActivitySearchIdRef.current = activitySearchId;
      reset(initialValues);

      // After form is reset, update template manager's tracking
      const timer = setTimeout(() => {
        resetInitialValues();
      }, 0);

      return () => clearTimeout(timer);
    }
    // Only run when activitySearchId changes
    // reset and resetInitialValues are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitySearchId]);

  const timeZone = watch('timeZone');

  // Reset timeZone field when tenant changes (useForm's defaultValues only apply on first render)
  // This ensures the form reflects the current tenant's timezone even if tenant changes without a full reload
  useEffect(() => {
    const newTimeZone = getTenantTimezone(tenant);
    // Only update if the timezone actually changed to avoid unnecessary resets
    if (timeZone !== newTimeZone) {
      setValue('timeZone', newTimeZone, { shouldDirty: false });
    }
  }, [tenant, timeZone, setValue]);
  const scope = watch('scope');
  const scopeIds = watch('scopeIds');
  const assignee = watch('assignee');
  const scheduledStartDate = watch('scheduledStartDate');
  const scheduledStartTime = watch('scheduledStartTime');
  const scheduledEndDate = watch('scheduledEndDate');
  const scheduledEndTime = watch('scheduledEndTime');
  const isJobRepeat = watch('repeat.isRepeat');
  const locationId = scopeIds.length
    ? [scope, scopeIds[0]].join(':')
    : undefined;

  // Block navigation when form is dirty OR when creating from asset (always has meaningful content)
  // But don't block if we're already navigating away after form submission
  const shouldPrompt =
    !navigateTo && !isSubmitting && (isDirty || !!initialAsset);

  // Memoize expensive WHERE bubble computations
  const whereInfo = useMemo(() => {
    const selectedLocations =
      scopeIds.length > 1 ? `${scopeIds.length} Selected` : null;
    const location =
      scopeIds.length === 1
        ? sites.find((site) => site.id === scopeIds[0])
        : null;
    const where = selectedLocations || location?.name;

    return { selectedLocations, location, where };
  }, [scopeIds, sites]);

  const { selectedLocations, location, where } = whereInfo;

  // Memoize filtered assets based on scopeIds to avoid duplicate filtering
  const filteredAssets = useMemo(() => {
    return allAssets.filter((asset) => {
      // Filter by scope if scopeIds are set
      if (scopeIds.length > 0) {
        return scopeIds.includes(asset.deployment?.location?.id ?? '');
      }
      return true;
    });
  }, [allAssets, scopeIds]);
  const isBulkAssetFlow = !!bulkAssetJobCreation;

  // Collect all assets that will be added to this job
  // This includes assets from initialAsset (single asset) and bulkAssetJobCreation (multiple assets)
  const jobAssets = useMemo(() => {
    const assets: Array<{
      id: string;
      name: string;
      type?: string;
      image?: string | null;
      deployment?: {
        location?: { id: string; name: string };
        space?: { id: string; name: string };
        image?: string | null;
      };
    }> = [];

    // Add single asset from initialAsset
    if (initialAsset) {
      // Try to find full asset data from allAssets
      const fullAsset = allAssets.find((a) => a.id === initialAsset.id);
      if (fullAsset) {
        assets.push({
          id: fullAsset.id,
          name: fullAsset.name,
          type: fullAsset.type,
          image: fullAsset.image,
          deployment: fullAsset.deployment
            ? {
                location: fullAsset.deployment.location
                  ? {
                      id: fullAsset.deployment.location.id,
                      name: fullAsset.deployment.location.name,
                    }
                  : undefined,
                space: fullAsset.deployment.space
                  ? {
                      id: fullAsset.deployment.space.id,
                      name: fullAsset.deployment.space.name,
                    }
                  : undefined,
                image: fullAsset.deployment.image,
              }
            : undefined,
        });
      } else {
        // Fallback to initialAsset data if not found in allAssets
        assets.push({
          id: initialAsset.id,
          name: initialAsset.name,
          type: initialAsset.type,
          deployment: initialAsset.deployment,
        });
      }
    }

    // Add bulk assets from bulkAssetJobCreation
    if (bulkAssetJobCreation?.assets?.length) {
      bulkAssetJobCreation.assets.forEach((assetData) => {
        // Try to find full asset data from allAssets
        const fullAsset = allAssets.find((a) => a.id === assetData.id);
        if (fullAsset) {
          // Only add if not already added (avoid duplicates)
          if (!assets.some((a) => a.id === fullAsset.id)) {
            assets.push({
              id: fullAsset.id,
              name: fullAsset.name,
              type: fullAsset.type,
              image: fullAsset.image,
              deployment: fullAsset.deployment
                ? {
                    location: fullAsset.deployment.location
                      ? {
                          id: fullAsset.deployment.location.id,
                          name: fullAsset.deployment.location.name,
                        }
                      : undefined,
                    space: fullAsset.deployment.space
                      ? {
                          id: fullAsset.deployment.space.id,
                          name: fullAsset.deployment.space.name,
                        }
                      : undefined,
                    image: fullAsset.deployment.image,
                  }
                : undefined,
            });
          }
        } else {
          // Fallback to assetData if not found in allAssets
          // Only add if not already added
          if (!assets.some((a) => a.id === assetData.id)) {
            assets.push({
              id: assetData.id,
              name: assetData.name,
              type: assetData.type,
              deployment: assetData.deploymentLocationId
                ? {
                    location: assetData.deploymentLocationName
                      ? {
                          id: assetData.deploymentLocationId,
                          name: assetData.deploymentLocationName,
                        }
                      : undefined,
                    space: assetData.deploymentSpaceName
                      ? {
                          id: '',
                          name: assetData.deploymentSpaceName,
                        }
                      : undefined,
                  }
                : undefined,
            });
          }
        }
      });
    }

    return assets;
  }, [initialAsset, bulkAssetJobCreation, allAssets]);

  // Create a stable key from jobAssets IDs to detect ID changes even when count stays the same
  const jobAssetsIdsKey = useMemo(() => {
    return jobAssets
      .map((asset) => asset.id)
      .sort()
      .join(',');
  }, [jobAssets]);

  // Auto-select known assets when using asset-driven templates (bulk flow or single asset)
  useEffect(() => {
    if (!assetDrivenState.isAssetDriven) {
      assetSelectionInitializedRef.current = false;
      return;
    }

    if (
      assetSelectionInitializedRef.current ||
      assetDrivenState.selectedAssetIds.length > 0
    ) {
      return;
    }

    const defaultAssetIds = jobAssets.map((asset) => asset.id);
    if (defaultAssetIds.length > 0) {
      setAssetDrivenState((prev) => ({
        ...prev,
        selectedAssetIds: defaultAssetIds,
      }));
      assetSelectionInitializedRef.current = true;
    }
    // jobAssets is intentionally excluded - we depend on jobAssetsIdsKey instead to detect ID changes
    // even when count stays the same, and to avoid infinite loops from array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assetDrivenState.isAssetDriven,
    assetDrivenState.selectedAssetIds.length,
    jobAssetsIdsKey, // Stable key derived from sorted asset IDs - detects ID changes even when count stays the same
  ]);

  // Memoize WHO computation
  const who = useMemo(() => {
    return (
      assignee &&
      contacts
        .filter(({ id }) => assignee.includes(id))
        .map(({ name }) => name)
        .join(', ')
    );
  }, [assignee, contacts]);

  // Determine if assignee is a team member (for sharing permissions)
  // null = no assignee, true = all assignees are team members, false = at least one is external
  const assigneeIsTeamMember = useMemo(() => {
    if (!assignee || assignee.length === 0) return null;
    const selectedContacts = contacts.filter(({ id }) => assignee.includes(id));
    if (selectedContacts.length === 0) return null;
    // If all selected assignees are team members, return true
    return selectedContacts.every((c) => c.isTeamMember);
  }, [assignee, contacts]);

  const when = (() => {
    // scheduledStartDate/scheduledEndDate are YYYY-MM-DD strings (property-local)
    // Parse them directly without timezone conversion to avoid browser timezone issues
    if (scheduledStartDate) {
      try {
        // Parse YYYY-MM-DD string directly
        const parsed = parse(scheduledStartDate, 'yyyy-MM-dd', new Date());
        if (!isNaN(parsed.getTime())) {
          return format(parsed, 'MMM d, yyyy');
        }
      } catch {
        // Fallback to original if parsing fails
        return scheduledStartDate;
      }
    } else if (scheduledEndDate) {
      try {
        const parsed = parse(scheduledEndDate, 'yyyy-MM-dd', new Date());
        if (!isNaN(parsed.getTime())) {
          return `end at: ${format(parsed, 'MMM d, yyyy')}`;
        }
      } catch {
        return `end at: ${scheduledEndDate}`;
      }
    }
    return null;
  })();
  const guestyEnabled = data.integrations.includes('guesty');

  const isMultipleJobCreation = !!(scopeIds.length > 1 || isJobRepeat);
  const notifyDefault = !isMultipleJobCreation;

  const [openPanelIdx, setOpenPanelIdx] = useState<number>(() => {
    if (createFrom?.job?.location) return 1;
    if (createFromActivityMessage || createFromActivityAttachments()) return 3;
    // Open "What" panel when creating from asset since we have pre-populated data
    if (initialAsset) return 3;
    return 0;
  });
  const [animateFieldId, setAnimateFieldId] = useState<string | null>(null);

  const handlePanelToggle = useCallback(
    async (panelIndex: number | undefined) => {
      // Handle the case when no panels are open (-1) or undefined
      const nextPanelIndex = panelIndex === undefined ? -1 : panelIndex;

      // If we're clicking on a different panel or closing the current panel
      if (nextPanelIndex !== openPanelIdx) {
        // Validate fields based on which panel is currently open
        switch (openPanelIdx) {
          case 0: // Where panel
            await form.trigger('scopeIds');
            break;
          case 2: // When panel
            if (watch('repeat.isRepeat')) {
              await form.trigger('repeat');
            }
            break;
        }
      }
      setOpenPanelIdx(nextPanelIndex);
      // Scroll to the panel if it's being opened
      if (panelIndex !== null && panelIndex !== undefined) {
        setTimeout(() => {
          const panels = document.querySelectorAll('.greycard');
          panels[panelIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 300);
      }
    },
    [openPanelIdx, form.trigger, watch]
  );

  const focusTasksSection = useCallback(() => {
    setOpenPanelIdx(3);
    setTimeout(() => {
      const section = document.querySelector('[data-job-tasks-section]');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 120);
  }, [setOpenPanelIdx]);

  const handleSuccessCreation = (jobId: string, status: string) => {
    if (pageName === 'activity') {
      setNavigateTo(
        status === JobStatus.Created
          ? `../jobs/${jobId}/edit`
          : `../jobs/${jobId}`
      );
    } else {
      setNavigateTo(
        status === JobStatus.Created ? `../${jobId}/edit` : `../${jobId}`
      );
    }

    toast.success('Job successfully created');
  };

  const submit = async (status: JobStatus, options: SubmitOptions = {}) => {
    await handleSubmit(
      async (data) => {
        setIsSubmitting(true);

        try {
          // Process tasks before submission to ensure attributes are preserved
          const processedTasks = taskState[0]
            .filter((task) => !task._destroy)
            .map((task) => {
              // Create a complete copy of the task with all properties, excluding invalid GraphQL fields
              const processedTask = { ...task };

              // Remove fields that are not part of TaskInput type
              delete (
                processedTask as TaskInput & {
                  assetId?: string;
                  templateTaskId?: string;
                }
              ).assetId;
              delete (
                processedTask as TaskInput & {
                  assetId?: string;
                  templateTaskId?: string;
                }
              ).templateTaskId;

              // Make sure attributes is included in the submission
              if (task.attributes) {
                processedTask.attributes = [...task.attributes];
              }

              // Ensure Attribute task configuration is properly formatted
              if (task.type === 'Attribute' && task.attribute?.attributes) {
                if (!processedTask.attribute) {
                  processedTask.attribute = { attributes: [] };
                }
                processedTask.attribute.attributes = task.attribute.attributes;
              }

              return processedTask;
            });

          // Handle template creation for repeat automation BEFORE job creation
          // NOTE: When an automation with `on: 'repeat'` is created, the backend immediately fires
          // `fireRepeatAutomationEvent`, which creates jobs based on the automation's schedule.
          // If job creation fails after this succeeds, the automation will persist and will have
          // already created (or will create) jobs according to its schedule. Duplicate detection
          // prevents duplicate jobs, but unwanted jobs may still be created if the user cancels/aborts.
          // There is no deleteAutomation mutation available for rollback.
          // This is a known limitation that should be addressed with
          // deleteAutomation mutation for cleanup

          let automationId: string | null = null;
          if (data.createRepeatAutomation) {
            if (!data.repeat.isRepeat) {
              toast.error(
                'Repeats must be enabled before creating an automation.'
              );
              setIsSubmitting(false);
              return;
            }
            try {
              // Extract job config for template matching
              const jobConfig: JobConfig = {
                name: data.name,
                notes: data.notes ? JSON.stringify(data.notes) : null,
                tags: data.tags || null,
                includeAttributes: data.includeAttributes || null,
                taskCount: processedTasks.length,
              };

              const automationResult = await createTemplateAndAutomation({
                jobConfig,
                processedTasks,
                selectedTemplate,
                isTemplateModified,
                createJobTemplate,
                createAutomation,
                formData: {
                  name: data.name,
                  notes: data.notes ?? null,
                  tags: data.tags || null,
                  includeAttributes: data.includeAttributes || null,
                  jobAttributes: data.jobAttributes || null,
                  attachments: (data.attachments || []).map((att) => ({
                    id: att.id,
                    kind: att.kind,
                    name: att.name,
                    preview: att.preview,
                  })),
                  guestyCleaningStatus: data.guestyCleaningStatus,
                  repeat: {
                    numOfRepeats: data.repeat.numOfRepeats,
                    value: data.repeat,
                  },
                  scopeIds: data.scopeIds,
                  owner: data.owner || null,
                  assignee: data.assignee || null,
                  scheduledStartTime: data.scheduledStartTime || null,
                  scheduledStartDate: data.scheduledStartDate || null,
                  timeZone: data.timeZone,
                },
              });
              if (!automationResult.automationId) {
                toast.error(
                  'Automation could not be created. Please try again.'
                );
                setIsSubmitting(false);
                return;
              }
              automationId = automationResult.automationId;
            } catch (error) {
              // Handle specific error types with appropriate messages
              if (error instanceof TemplateAutomationError) {
                console.error(
                  `Template/Automation creation error (${error.step}):`,
                  error.originalError || error
                );
                toast.error(error.message);
              } else {
                console.error(
                  'Unexpected error during template/automation creation:',
                  error
                );
                toast.error(
                  'An unexpected error occurred while creating template/automation. Please try again.'
                );
              }
              setIsSubmitting(false);
              return;
            }
          }

          // Exclude createRepeatAutomation from mutation input - we handle it in the frontend
          const {
            createRepeatAutomation: _createRepeatAutomation,
            ...jobData
          } = data;
          const mutationInput = {
            ...jobData,
            automationId: automationId ?? jobData.automationId ?? null,
            attachments: (
              createFromActivityAttachments() ?? jobData.attachments
            ).map(toAttachmentInput),
            scheduledStartDate: jobData.scheduledStartDate || null,
            scheduledStartTime: jobData.scheduledStartTime || null,
            scheduledEndDate: jobData.scheduledEndDate || null,
            scheduledEndTime: jobData.scheduledEndTime || null,
            // Stringify notes from RawDraftContentState to JSON string for server
            notes: jobData.notes ? JSON.stringify(jobData.notes) : null,
            tasks: processedTasks,
            status,
            notify: options.notify,
            comment: options.comment,
            sendMeACopy: options.sendMeACopy === true,
            includePdf: options.includePdf === true,
            guestyCleaningStatus: jobData.guestyCleaningStatus ? 'clean' : null,
            repeat: repeatToInput(jobData.repeat),
          };
          const res = await createJobs({
            input: mutationInput,
          });

          if (res.error) {
            console.error('GraphQL Error Details:', {
              message: res.error.message,
              graphQLErrors: res.error.graphQLErrors,
              networkError: res.error.networkError,
              mutationInput: mutationInput,
            });
            toast.error(
              'Error: ' + res.error.message.replace('[GraphQL] ', '')
            );
            setIsSubmitting(false);
            return;
          }

          if (res.data) {
            // Store job dates for automatic date filter adjustment
            // This allows the jobs view to expand filters if the job is outside current range
            storeCreatedJobDates(res.data.createJobs);

            // TODO too many conditionals... tidy up
            if (createFrom?.job) {
              toast.success('Job saved successfully');
              setNavigateTo('..');
            } else if (window.location.href.includes('/jobs/calendar')) {
              if (res.data.createJobs.length > 1) {
                toast.success(
                  `${res.data.createJobs.length} jobs successfully created`
                );
                setNavigateTo('..');
              } else {
                toast.success(`Job successfully created`);
                // Navigate clicking 'Save Draft' btn to edit form
                setNavigateTo(
                  status === JobStatus.Created
                    ? `../${res.data.createJobs[0].id}/edit`
                    : `../${res.data.createJobs[0].id}`
                );
              }
            }
            // else if (onSuccess) {
            //   onSuccess(res.data);

            //   if (res.data.createJobs.length > 1) {
            //     toast.success(
            //       `${res.data.createJobs.length} jobs successfully created`
            //     );
            //     setNavigateTo('..');
            //   } else {
            //     toast.success(`Job successfully created`);
            //     // Navigate clicking 'Save Draft' btn to edit form
            //     setNavigateTo(
            //       status === JobStatus.Created
            //         ? `../${res.data.createJobs[0].id}/edit`
            //         : `../${res.data.createJobs[0].id}`
            //     );
            //   }
            // }
            else if (popupOnSuccess) {
              popupOnSuccess(res.data);
            } else {
              // default success handler
              setTimeout(() => resetInitialValues(), 0);
              handleSuccessCreation(res.data.createJobs[0].id, status);
            }
          }
        } catch (error) {
          console.error('Unexpected error during job creation:', error);
          toast.error('An unexpected error occurred. Please try again.');
          setIsSubmitting(false);
        }
      },
      (errors) => {
        const errorMessage = 'Please fill out all required fields';
        toast.warning(errorMessage, {
          theme: 'colored',
        });

        // Find the first panel with an error
        let firstErrorPanel = null;
        if (errors.scopeIds) {
          firstErrorPanel = 0; // WHERE panel
        } else if (errors.repeat?.numOfRepeats) {
          firstErrorPanel = 2; // WHEN panel
        } else if (errors.name) {
          firstErrorPanel = 3; // WHAT panel
        }

        if (firstErrorPanel !== null) {
          const currentPanelHasError =
            (errors.scopeIds && openPanelIdx === 0) ||
            (errors.repeat?.numOfRepeats && openPanelIdx === 2) ||
            (errors.name && openPanelIdx === 3);

          if (currentPanelHasError) {
            if (openPanelIdx === 0 && errors.scopeIds) {
              setAnimateFieldId('scopeIds');
            } else if (openPanelIdx === 2 && errors.repeat?.numOfRepeats) {
              setAnimateFieldId('repeat.numOfRepeats');
            } else if (openPanelIdx === 3 && errors.name) {
              setAnimateFieldId('name');
            }
            // Remove animation class after it completes
            setTimeout(() => setAnimateFieldId(null), 1820); // Animation duration + small buffer
          } else if (
            !currentPanelHasError &&
            firstErrorPanel !== openPanelIdx
          ) {
            // First close any open panel
            setOpenPanelIdx(-1);

            // Then open the error panel after a small delay
            setTimeout(() => {
              setOpenPanelIdx(firstErrorPanel);

              // Scroll after panel is opened
              setTimeout(() => {
                const panels = document.querySelectorAll('.greycard');
                panels[firstErrorPanel]?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }, 100);
            }, 50);
          }
        }
      }
    )();
  };

  const [task, setTask] = useState<TaskInput | null>(null);
  const [shownTaskType, setShownTaskType] = useState<string | null>(null);

  // Use a ref to track the current task IDs to avoid unnecessary effect runs
  /**
   * Task ID tracking for detecting when tasks are regenerated
   * Stores a comma-separated string of sorted task IDs to detect changes
   */
  const taskIdsRef = useRef<string>('');

  /**
   * CRITICAL: Task Opening Protection Flag
   *
   * This ref prevents the "blinking" bug in asset-driven templates where tasks
   * would open and immediately close due to race conditions.
   *
   * LIFECYCLE:
   * 1. Set to task ID when user clicks to open a task (in handleShowTask)
   * 2. Kept set for 300ms protection window (managed by effect below)
   * 3. Cleared after window expires or when task is closed
   *
   * USAGE:
   * - Asset-driven template effect checks this before closing tasks
   * - If set, tasks are NOT closed even if assets change
   * - This prevents closing a task that's in the process of opening
   *
   * WHY A REF (not state):
   * - Refs don't trigger re-renders, avoiding unnecessary effect runs
   * - Synchronous updates (no React batching delays)
   * - Can be checked immediately in effects without stale closure issues
   *
   * DO NOT remove or modify without understanding the full race condition fix above.
   */
  const openingTaskRef = useRef<string | null>(null);

  /**
   * Effect: Handle Task Opening (showTask changes)
   *
   * This effect runs when the user clicks to open a task (showTask changes).
   *
   * RESPONSIBILITIES:
   * 1. Set the opening flag to protect against race conditions
   * 2. Find the task in taskState and update local task/type state
   * 3. Update the task IDs ref for change detection
   *
   * IMPORTANT BEHAVIORS:
   * - If task is found: Update state and clear opening flag immediately
   * - If task NOT found: Don't close yet - wait for taskState to update
   *   (The task might be in the process of being added to taskState)
   *
   * This works in conjunction with the effect below that watches taskState changes.
   */
  useEffect(() => {
    if (!showTask) {
      setTask(null);
      setShownTaskType(null);
      return;
    }

    // When showTask changes, immediately set the opening flag
    openingTaskRef.current = showTask;

    // Try to find the task
    const foundTask = taskState[0].find((task) => task.id === showTask);

    if (foundTask) {
      // Task exists, update it
      setTask(foundTask);
      setShownTaskType(foundTask.type || null);
      openingTaskRef.current = null;

      // Update task IDs ref
      const currentTaskIds = taskState[0]
        .filter((t) => !t._destroy)
        .map((t) => t.id)
        .sort()
        .join(',');
      taskIdsRef.current = currentTaskIds;
    }
    // If task not found, don't close yet - wait for taskState to update
  }, [showTask]);

  /**
   * Effect: Handle Task State Changes (taskState updates/regeneration)
   *
   * This effect runs when tasks are added, removed, or regenerated (taskState changes).
   *
   * RESPONSIBILITIES:
   * 1. Keep the displayed task in sync with taskState
   * 2. Detect when tasks are regenerated (IDs change)
   * 3. Close the task editor if the task no longer exists
   *
   * CRITICAL LOGIC FOR ASSET-DRIVEN TEMPLATES:
   * - When tasks are regenerated (e.g., assets change), task IDs change
   * - If a task is open and its ID is no longer in taskState, we need to close it
   * - BUT: If we're in the middle of opening a task (openingTaskRef is set), DON'T close it
   * - This prevents the "blinking" bug where tasks open and immediately close
   *
   * SMART CLOSING LOGIC:
   * - Only close if task IDs actually changed (not just reference changes)
   * - Check openingTaskRef to avoid closing during the opening process
   * - This works with the asset-driven template effect above to prevent race conditions
   *
   * WHY TWO EFFECTS (showTask + taskState):
   * - showTask effect: Handles user-initiated opening (click to open)
   * - taskState effect: Handles system-initiated changes (regeneration, updates)
   * - Separating them makes the logic clearer and prevents infinite loops
   */
  useEffect(() => {
    if (!showTask) {
      taskIdsRef.current = '';
      openingTaskRef.current = null;
      return;
    }

    const foundTask = taskState[0].find((task) => task.id === showTask);

    if (foundTask) {
      // Task exists, update it
      setTask(foundTask);
      setShownTaskType(foundTask.type || null);
      openingTaskRef.current = null;

      // Update task IDs ref
      const currentTaskIds = taskState[0]
        .filter((t) => !t._destroy)
        .map((t) => t.id)
        .sort()
        .join(',');
      taskIdsRef.current = currentTaskIds;
    } else {
      // Task not found
      const isOpening = openingTaskRef.current === showTask;

      if (isOpening) {
        // Still opening, don't close yet
        return;
      }

      // Not opening, check if task IDs actually changed
      const currentTaskIds = taskState[0]
        .filter((t) => !t._destroy)
        .map((t) => t.id)
        .sort()
        .join(',');
      const taskIdsChanged =
        taskIdsRef.current !== '' && taskIdsRef.current !== currentTaskIds;

      if (taskIdsChanged) {
        // Task IDs changed and task not found - it was regenerated, close it
        taskIdsRef.current = currentTaskIds;
        setShowTask(null);
        setTask(null);
        setShownTaskType(null);
      }
      // If taskIds haven't changed, don't close (prevents closing on reference changes)
    }
  }, [taskState, showTask, setShowTask]);

  /**
   * CRITICAL: Task Opening Protection Window
   *
   * This effect manages the openingTaskRef flag that prevents race conditions.
   *
   * WHAT IT DOES:
   * - When a task is opened (showTask changes), set openingTaskRef immediately
   * - Keep the flag set for 300ms to create a "protection window"
   * - During this window, the asset-driven template effect (above) won't close the task
   * - After 300ms, clear the flag so normal task closing can resume
   *
   * WHY 300MS:
   * - Gives React time to process state updates and render the task form
   * - Accounts for potential re-renders and async state updates
   * - Long enough to prevent race conditions, short enough to not impact UX
   * - Increased from 200ms to 300ms for better stability
   *
   * DO NOT REDUCE THIS TIMEOUT without thorough testing across:
   * - Different browsers (Chrome, Safari, Firefox)
   * - Different devices (desktop, mobile)
   * - Slow connections/devices
   * - Rapid user interactions (clicking multiple tasks quickly)
   */
  useEffect(() => {
    if (showTask) {
      openingTaskRef.current = showTask;
      // Clear the flag after a delay to allow taskState to update
      // This gives the task time to appear in taskState before we check if it exists
      const timeoutId = setTimeout(() => {
        if (openingTaskRef.current === showTask) {
          openingTaskRef.current = null;
        }
      }, 300); // Increased from 200ms to 300ms for better stability
      return () => clearTimeout(timeoutId);
    } else {
      openingTaskRef.current = null;
    }
  }, [showTask]);

  const taskSection = task ? (
    <CustomTaskForm
      key={task.id}
      locationId={locationId}
      task={task}
      onApply={updateTask}
      onClose={() => setShowTask(null)}
      onDelete={deleteTask}
      scopeIds={scopeIds}
      taskType={shownTaskType}
      updateTaskType={(id, type) => handleUpdateTaskType(id, type)}
      showTaskOptions
      isAssetJob={animationSource === 'asset'}
      assigneeIsTeamMember={assigneeIsTeamMember}
      isAssetDrivenTemplate={assetDrivenState.isAssetDriven}
    />
  ) : null;

  const templateOptions: Option[] = templates.map(({ name: value }) => ({
    value,
    label: value,
  }));

  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  /**
   * CRITICAL: Task Opening Handler
   *
   * This is the ONLY function that should be used to open tasks in asset-driven templates.
   *
   * SYNCHRONIZATION STRATEGY:
   * 1. Set openingTaskRef.current = id FIRST (synchronous, happens immediately)
   * 2. Then call setShowTask(id) (triggers React state update)
   * 3. The ref being set BEFORE state update is crucial for race condition prevention
   *
   * WHY THIS ORDER MATTERS:
   * - React state updates are asynchronous and may trigger multiple effects
   * - The asset-driven template effect checks openingTaskRef before closing tasks
   * - By setting the ref first, we ensure it's already set when effects run
   * - This creates a "lock" that prevents the task from being closed during opening
   *
   * WHAT HAPPENS AFTER:
   * - The effect above (Task Opening Protection Window) clears openingTaskRef after 300ms
   * - This allows normal task closing behavior to resume
   * - The 300ms window is enough time for React to stabilize the task form
   *
   * DO NOT MODIFY THIS FUNCTION without understanding the full synchronization flow.
   * DO NOT call setShowTask directly elsewhere - always use this handler.
   */
  const handleShowTask = useCallback(
    (id: string) => {
      // Set the opening flag synchronously BEFORE setting showTask
      // This prevents the useEffect from closing the task before it's marked as opening
      openingTaskRef.current = id;
      setShowTask(id);
    },
    [setShowTask]
  );

  const handleUpdateTaskType = useCallback(
    (taskId: string, taskType: string) => {
      const [, setTasks] = taskState;
      const tasksListCopy = taskState[0].slice();
      const taskIndex = tasksListCopy.findIndex((task) => task.id === taskId);
      if (taskIndex < 0) return;
      tasksListCopy[taskIndex].type = taskType;
      setTasks(tasksListCopy);
    },
    [taskState]
  );

  const isNewTask = useCallback(
    (taskId: string) => {
      const [tasks] = taskState;
      return !tasks.some((task) => task.id === taskId);
    },
    [taskState]
  );

  // Note: allSelectedAssets was removed as it's unused.
  // Asset-driven templates only work with asset types, not specific asset IDs,
  // so there's no need to combine jobAssets with asset-driven selected assets.

  const taskAssetCoverage = useMemo(() => {
    const [tasks] = taskState;
    const covered = new Set<string>();

    tasks
      .filter((task) => !task._destroy)
      .forEach((task) => {
        const assetIds = task.asset?.assets || [];
        assetIds.forEach((assetId) => {
          if (assetId) {
            covered.add(assetId);
          }
        });
      });

    return covered;
  }, [taskState[0]]);

  // Calculate uncovered assets count for standard variant (to conditionally show section)
  const uncoveredAssetsCount = useMemo(() => {
    if (
      !assetDrivenState.isAssetDriven ||
      !selectedTemplate ||
      isBulkAssetFlow ||
      assetDrivenState.selectedAssetIds.length === 0
    ) {
      return 0;
    }

    // Get selected assets
    const selectedAssets = allAssets.filter((asset) =>
      assetDrivenState.selectedAssetIds.includes(asset.id)
    );

    // Calculate task previews to determine which assets have matching tasks
    const { assetMatches } = calculateTaskPreviews(
      selectedTemplate,
      selectedAssets
    );

    // Count assets without matching tasks and not covered by manual tasks
    const uncoveredCount = assetMatches.filter(
      (m) => !m.hasMatchingTasks && !taskAssetCoverage.has(m.asset.id)
    ).length;

    return uncoveredCount;
  }, [
    assetDrivenState.isAssetDriven,
    assetDrivenState.selectedAssetIds,
    selectedTemplate,
    allAssets,
    isBulkAssetFlow,
    taskAssetCoverage,
  ]);

  /**
   * Function to create asset task
   *
   * Uses functional state updates to prevent stale closure issues.
   * When multiple tasks are created quickly, functional updates ensure
   * each new task is added to the LATEST state, not a stale snapshot.
   *
   * This does NOT interfere with the task opening race condition fix above.
   * The openingTaskRef protection works regardless of how tasks are added.
   */
  const handleCreateAssetTask = useCallback(
    (assetId: string) => {
      const asset = allAssets.find((a) => a.id === assetId);
      if (!asset) return;

      const [, setTasks] = taskState;
      const editorState = EditorState.createEmpty();
      const defaultDescription = convertToRaw(editorState.getCurrentContent());

      const newTask: TaskInput = {
        id: nanoid(),
        type: 'Asset',
        name: `${selectedTemplate?.name || 'Task'} ${asset.name}`,
        description: JSON.stringify(defaultDescription),
        attributes: [],
        asset: {
          selection: 'specific',
          assets: [assetId],
          spaces: asset.deployment?.space?.name
            ? [asset.deployment.space.name]
            : [],
          assetTypes: asset.type ? [asset.type] : [],
          deploy: 'Job',
          allow: ['edit'],
        },
        _destroy: false,
      };

      // Use functional state update to avoid stale closure issues
      // This ensures we're always adding to the LATEST task list,
      // even if multiple tasks are created in quick succession
      setTasks((prevTasks) => {
        const newTasks = [...prevTasks, newTask];
        // Sync with React Hook Form using the same computed array
        // to ensure RHF and component state stay in sync
        setValue('tasks', newTasks);
        return newTasks;
      });
    },
    [allAssets, taskState, setValue, selectedTemplate]
  );

  return (
    <FormProvider {...form}>
      <div
        data-usetiful='create-job-form'
        className={classNames(
          'flex h-full items-stretch justify-end overflow-y-auto',
          isPopup && 'flex-col'
        )}
      >
        <SideLayout>
          <SideLayout.Head onClose={onClose} iconClass={'x'}>
            Create Job
          </SideLayout.Head>
          <SideLayout.Body className='px-4 pb-96 lg:pb-44'>
            <Prompt when={shouldPrompt} cancel='Keep Editing' />

            <Accordion
              defaultOpen={openPanelIdx}
              onToggledPanel={handlePanelToggle}
              openPanel={openPanelIdx}
            >
              {/* Always show WHERE section, but lock it when locationId is provided */}
              <Accordion.GreyCard error={!!errors.scopeIds}>
                <Accordion.Button
                  dataUsetiful='Where'
                  summary={where}
                  help={errors.scopeIds ? 'Place is required' : 'Place'}
                  error={!!errors.scopeIds}
                >
                  Where
                </Accordion.Button>
                <Accordion.Panel>
                  {scope === CreateJobScope.Site && (
                    <div
                      className={classNames({
                        'animate-shake': animateFieldId === 'scopeIds',
                      })}
                    >
                      <Controller
                        name='scopeIds'
                        control={control}
                        render={({ field, fieldState }) => (
                          <FindAddSite
                            label={t('form.site')}
                            error={fieldState.error?.message}
                            value={field.value}
                            onChange={(selected) => {
                              if (selected && selected.length > 1) {
                                informMultipleSitesSelected(selected.length);
                              }
                              if (
                                selected &&
                                selected.length &&
                                selected[0].id
                              ) {
                                field.onChange(selected.map(({ id }) => id));
                                //next()
                                // Force validation to run
                                setTimeout(() => form.trigger('scopeIds'), 0);
                                return;
                              }
                              field.onChange([]);
                            }}
                            multiple
                            disabled={!!props.locationId}
                          />
                        )}
                      />
                      {(props.locationId || initialAsset) && (
                        <p className='mt-2 text-sm text-secondary'>
                          Location is pre-selected from{' '}
                          {initialAsset ? 'the asset' : 'your current place'}
                        </p>
                      )}
                    </div>
                  )}
                </Accordion.Panel>
              </Accordion.GreyCard>
              <Accordion.GreyCard>
                <Accordion.Button
                  dataUsetiful='Who'
                  summary={who}
                  help='Unassigned'
                >
                  Who
                </Accordion.Button>
                <Accordion.Panel>
                  <Controller
                    name='owner'
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <FindAddContact
                        controlClassName='bg-white'
                        optionsBoxHeight={
                          isMobile
                            ? 'max-h-[calc(100vh-285px)]'
                            : 'max-h-[calc(100vh-450px)]'
                        }
                        label={t('form.owner')}
                        // contacts={contacts.filter((c) =>
                        //   c.relationship?.includes(ContactRelationship.Staff)
                        // )}
                        error={error?.message}
                        value={
                          value
                            ? contacts.filter(({ id }) => value.includes(id))
                            : []
                        }
                        onChange={(newValue) => {
                          onChange(
                            newValue && newValue.length > 0
                              ? newValue.map(({ id }) => id)
                              : []
                          );
                        }}
                        isTeamMember
                        multiple
                        isAbsolute={false}
                      />
                    )}
                  />
                  <Controller
                    name='assignee'
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <FindAddContact
                        controlClassName='bg-white'
                        optionsBoxHeight={
                          isMobile
                            ? 'max-h-[calc(100vh-285px)]'
                            : 'max-h-[calc(100vh-450px)]'
                        }
                        label={t('form.assignee')}
                        // contacts={contacts}
                        error={error?.message}
                        value={
                          value?.length
                            ? contacts.filter(({ id }) => value.includes(id))
                            : []
                        }
                        onChange={(newValue) => {
                          if (newValue && newValue.length > 0) {
                            onChange(newValue.map(({ id }) => id));
                          } else {
                            onChange(null);
                          }
                        }}
                        multiple
                        isAbsolute={false}
                      />
                    )}
                  />
                  <Controller
                    name='include'
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <FindAddContact
                        controlClassName='bg-white'
                        optionsBoxHeight={
                          isMobile
                            ? 'max-h-[calc(100vh-285px)]'
                            : 'max-h-[calc(100vh-450px)]'
                        }
                        label={t('form.include')}
                        // contacts={contacts}
                        error={error?.message}
                        value={
                          value
                            ? contacts.filter(({ id }) => value.includes(id))
                            : []
                        }
                        onChange={(newValue) => {
                          onChange(
                            newValue && newValue.length > 0
                              ? newValue.map(({ id }) => id)
                              : []
                          );
                        }}
                        multiple
                        hasSelectAll
                        isAbsolute={false}
                      />
                    )}
                  />
                </Accordion.Panel>
              </Accordion.GreyCard>
              <Accordion.GreyCard error={!!errors.repeat?.numOfRepeats}>
                <Accordion.Button
                  dataUsetiful='When'
                  summary={
                    errors.repeat?.numOfRepeats
                      ? 'Number of repeats is required'
                      : when
                  }
                  help={'Unscheduled'}
                  error={!!errors.repeat?.numOfRepeats}
                >
                  When
                </Accordion.Button>
                <Accordion.Panel>
                  <Accordion.PanelSection>
                    {/* <div className='mb-2.5 hidden gap-4'>
                      <label>
                        <input
                          {...register('scheduledStartStrict')}
                          type='radio'
                          value='1'
                        />{' '}
                        Start at
                      </label>
                      <label>
                        <input
                          {...register('scheduledStartStrict')}
                          type='radio'
                          value='0'
                        />
                        Start from
                      </label>
                    </div> */}
                    <div className='items flex flex-wrap gap-x-12 gap-y-1 pt-2'>
                      <div
                        className={classNames(
                          'max-w-md flex-1 pr-1 text-left',
                          scheduledStartDate || scheduledStartTime
                            ? 'grid grid-cols-[3fr_2fr_auto] gap-3'
                            : 'grid grid-cols-[3fr_2fr] gap-3'
                        )}
                      >
                        <Controller
                          control={control}
                          name='scheduledStartDate'
                          render={({ field: { value, onChange } }) => (
                            <Input
                              label={t('job:form.scheduleStart')}
                              type='date'
                              value={value}
                              onChange={(event) => {
                                onChange(event);
                                setValue('repeat.repeatOnMonth', '');
                              }}
                            />
                          )}
                        />
                        <div>
                          <Controller
                            control={control}
                            name='scheduledStartTime'
                            render={({ field: { value, onChange } }) => (
                              <TimePicker
                                label='Time'
                                focusValue='09:00'
                                value={value}
                                onChange={onChange}
                              />
                            )}
                          />
                        </div>
                        {(scheduledStartDate || scheduledStartTime) && (
                          <button
                            className='justify-self-end pt-2.5'
                            onClick={() => {
                              setValue('scheduledStartDate', '');
                              setValue('scheduledStartTime', '');
                            }}
                          >
                            <FontAwesomeIcon
                              className='h-5 w-5 text-brand transition-colors duration-300 hover:text-brand-darker'
                              icon={faCircleXmark}
                            />
                          </button>
                        )}
                      </div>
                      {/* <div className='mb-2.5 hidden gap-4'>
                        <label>
                          <input
                            {...register('scheduledEndStrict')}
                            type='radio'
                            value='1'
                          />
                          End at
                        </label>
                        <label>
                          <input
                            {...register('scheduledEndStrict')}
                            type='radio'
                            value='0'
                          />
                          End before
                        </label>
                      </div> */}
                      <div
                        className={classNames(
                          'max-w-md flex-1 pr-1 text-left',
                          scheduledEndDate || scheduledEndTime
                            ? 'grid grid-cols-[3fr_2fr_auto] gap-3'
                            : 'grid grid-cols-[3fr_2fr] gap-3'
                        )}
                      >
                        <Input
                          {...register('scheduledEndDate')}
                          label={t('job:form.scheduleEnd')}
                          type='date'
                        />
                        <div className=''>
                          <Controller
                            control={control}
                            name='scheduledEndTime'
                            render={({ field: { value, onChange } }) => (
                              <TimePicker
                                label='Time'
                                focusValue={getDefaultScheduledEndTime(
                                  watch('scheduledStartTime')
                                )}
                                value={value}
                                onChange={onChange}
                              />
                            )}
                          />
                        </div>
                        {(scheduledEndDate || scheduledEndTime) && (
                          <button
                            className='justify-self-end pt-2.5'
                            onClick={() => {
                              setValue('scheduledEndDate', '');
                              setValue('scheduledEndTime', '');
                            }}
                          >
                            <FontAwesomeIcon
                              className='h-5 w-5 text-brand transition-colors duration-300 hover:text-brand-darker'
                              icon={faCircleXmark}
                            />
                          </button>
                        )}
                      </div>
                    </div>

                    <JobsRepeat
                      label='Repeats'
                      className={classNames({
                        'animate-shake':
                          animateFieldId === 'repeat.numOfRepeats',
                      })}
                      onBlur={() => {
                        if (watch('repeat.isRepeat')) {
                          form.trigger('repeat');
                        }
                      }}
                    />
                    {watch('repeat.isRepeat') &&
                      watch('repeat.numOfRepeats') && (
                        <Input
                          label={`Create Automation to always maintain ${watch(
                            'repeat.numOfRepeats'
                          )} repeats of this job`}
                          type='checkbox'
                          dataUsetiful='create-repeat-automation'
                          {...register('createRepeatAutomation')}
                          className='flex w-full flex-grow-0 flex-row-reverse items-center justify-end gap-2 text-wrap'
                          growInput={false}
                        />
                      )}
                  </Accordion.PanelSection>
                </Accordion.Panel>
              </Accordion.GreyCard>
              <Accordion.GreyCard error={!!errors.name}>
                <Accordion.Button
                  dataUsetiful='What'
                  help={errors.name ? 'Job Name is required' : 'Description'}
                  summary={watch('name')}
                  error={!!errors.name}
                >
                  What
                </Accordion.Button>
                <Accordion.Panel>
                  {(selectedTemplate ||
                    (taskState[0].filter((task) => !task._destroy).length ===
                      0 &&
                      templateOptions.length > 0) ||
                    assetDrivenState.isAssetDriven) && (
                    <Accordion.PanelSection heading={t('translation:template')}>
                      {selectedTemplate ? (
                        <div className='mb-5 flex items-center justify-between rounded-md border border-outline-variant py-2 pl-2 pr-1'>
                          <div className='flex flex-1 items-center gap-2.5'>
                            <div className='flex items-start rounded-sm bg-[rgba(0,106,106,0.14)] p-1'>
                              <FontAwesomeIcon
                                icon={
                                  assetDrivenState.isAssetDriven
                                    ? faSitemap
                                    : faListCheck
                                }
                                className='flex h-5 w-5 items-center justify-center text-brand'
                              />
                            </div>
                            {selectedTemplate.name}
                            {/* Show asset-driven badge if applicable */}
                          </div>{' '}
                          {assetDrivenState.isAssetDriven && (
                            <AssetDrivenBadge />
                          )}
                          {/* TODO Create icon Button variant */}
                          <HelperPopup
                            icon={
                              <div
                                className='ml-1 flex cursor-pointer items-center justify-center rounded-full p-2 text-secondary hover:bg-grey-10 hover:text-brand-darker'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    selectedTemplate &&
                                    isTemplateModified(selectedTemplate)
                                  ) {
                                    setShowRemoveConfirmation(true);
                                  } else {
                                    removeTemplate();
                                    // Also reset asset-driven state
                                    setAssetDrivenState({
                                      isAssetDriven: false,
                                      selectedAssetIds: [],
                                    });
                                  }
                                }}
                              >
                                <FaXmark className='h-4 w-4' />
                              </div>
                            }
                          >
                            Remove Template
                          </HelperPopup>
                        </div>
                      ) : (
                        <UseTemplate
                          jobTemplates={templates}
                          taskState={taskState}
                          onUseTemplateSelect={handleTemplateSelect}
                        />
                      )}
                    </Accordion.PanelSection>
                  )}
                  {/* Task Preview for Asset-Driven Templates - Unified Interface */}
                  {assetDrivenState.isAssetDriven && selectedTemplate && (
                    <div data-asset-driven-preview>
                      <AssetDrivenJobPreview
                        template={selectedTemplate}
                        allAssets={allAssets}
                        availableAssets={
                          isBulkAssetFlow ? filteredAssets : undefined
                        }
                        selectedAssetIds={assetDrivenState.selectedAssetIds}
                        onAssetSelectionChange={handleAssetSelectionChange}
                        onCreateAssetTask={handleCreateAssetTask}
                        onViewTasks={focusTasksSection}
                        defaultLocationId={scopeIds[0]}
                        variant={isBulkAssetFlow ? 'bulk' : 'standard'}
                        coveredAssetIds={Array.from(taskAssetCoverage)}
                        renderAssetsSeparately={true}
                      />
                    </div>
                  )}
                  <Accordion.PanelSection heading='Summary'>
                    <Input
                      {...register('name', {
                        onBlur: () => form.trigger('name'),
                      })}
                      label={t('form.name')}
                      required
                      error={errors.name?.message}
                      autoFocus={!isMobile}
                      maxLength={250}
                      className={classNames('mb-4', {
                        'animate-shake': animateFieldId === 'name',
                      })}
                    />
                    <div>
                      <Label>{t('translation:notes')}</Label>
                      <Controller
                        name='notes'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Editor
                            content={value}
                            onUpdate={onChange}
                            placeholder='Add notes...'
                          />
                        )}
                      />
                    </div>

                    <div className={classNames({ 'mb-2': !guestyEnabled })}>
                      <Controller
                        name='attachments'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <ControlledUpload
                            id={`CreateJobForm`}
                            value={value ?? []}
                            onChange={onChange}
                            isPill={true}
                          />
                        )}
                      />
                    </div>

                    {guestyEnabled && (
                      <div className='mb-5 block'>
                        <label className='flex items-center gap-1.5 text-sm text-grey-50'>
                          <input
                            {...register('guestyCleaningStatus')}
                            type='checkbox'
                            className='h-4 w-4'
                          />
                          Update Guesty Cleaning Status
                        </label>
                      </div>
                    )}
                  </Accordion.PanelSection>
                  <Accordion.PanelSection>
                    <div className='pb-1 text-base font-semibold text-primary'>
                      {t('translation:location_attributes')}
                    </div>
                    <p className='pb-3 text-sm text-secondary'>
                      Information about the job location relevant to this job
                    </p>
                    <Controller
                      name='includeAttributes'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <AttributeSelect
                          entityType='Site'
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Accordion.PanelSection>
                  {ability.can('use', 'feat.job-attributes') && (
                    <Accordion.PanelSection>
                      <div className='pb-1 text-base font-semibold text-primary'>
                        Custom Fields
                      </div>
                      <p className='pb-3 text-sm text-secondary'>
                        Metrics you would like collected for this job
                      </p>
                      <Controller
                        name='jobAttributes'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <AttributeSelect
                            entityType='Job'
                            // label='Job attributes to collect'
                            value={value}
                            onChange={onChange}
                          />
                        )}
                      />
                    </Accordion.PanelSection>
                  )}
                  <Accordion.PanelSection heading={t('translation:tag_plural')}>
                    <Controller
                      name='tags'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <TagCombobox
                          entityType={TagEntityType.Job}
                          options={allTags}
                          value={
                            value
                              ? allTags.filter((t) => value.includes(t.id))
                              : []
                          }
                          onChange={(tags) => {
                            reload();
                            onChange(tags.map(({ id }) => id));
                          }}
                          hasLabel={false}
                        />
                      )}
                    />
                  </Accordion.PanelSection>
                  {/* Assets Section - Show before tasks for both variants */}
                  {assetDrivenState.isAssetDriven &&
                    selectedTemplate &&
                    assetDrivenState.selectedAssetIds.length > 0 &&
                    (isBulkAssetFlow || uncoveredAssetsCount > 0) && (
                      <Accordion.PanelSection
                        heading={
                          isBulkAssetFlow ? (
                            <div className='flex items-center justify-between'>
                              <span>
                                {t(
                                  'job:assetDrivenTemplate.assetsInJob',
                                  'Assets in this Job'
                                )}{' '}
                                ({assetDrivenState.selectedAssetIds.length})
                              </span>
                              {openAddAssetsDialog && (
                                <button
                                  type='button'
                                  onClick={openAddAssetsDialog}
                                  className='hover:bg-brand/5 flex items-center gap-1.5 rounded border border-brand bg-white px-3 py-1.5 text-sm font-medium text-brand transition-colors'
                                >
                                  <FontAwesomeIcon
                                    icon={faPlus}
                                    className='h-3.5 w-3.5'
                                  />
                                  {t(
                                    'job:assetDrivenTemplate.addAssets',
                                    'Add Assets'
                                  )}
                                </button>
                              )}
                            </div>
                          ) : (
                            t(
                              'job:assetDrivenTemplate.assetsWithoutTasks',
                              'Assets without Tasks'
                            )
                          )
                        }
                      >
                        <AssetDrivenAssetsSection
                          template={selectedTemplate}
                          allAssets={allAssets}
                          selectedAssetIds={assetDrivenState.selectedAssetIds}
                          onAssetSelectionChange={handleAssetSelectionChange}
                          onCreateAssetTask={handleCreateAssetTask}
                          variant={isBulkAssetFlow ? 'bulk' : 'standard'}
                          coveredAssetIds={Array.from(taskAssetCoverage)}
                          showOnlyUncoveredAssets={!isBulkAssetFlow}
                          showAddAssetsButton={isBulkAssetFlow}
                          onGetOpenDialog={(openDialog) => {
                            setOpenAddAssetsDialog(() => openDialog);
                          }}
                          availableAssets={
                            isBulkAssetFlow ? filteredAssets : undefined
                          }
                          defaultLocationId={scopeIds[0]}
                        />
                      </Accordion.PanelSection>
                    )}
                  <Accordion.PanelSection
                    heading='Tasks'
                    data-job-tasks-section
                  >
                    <div className='mb-5'>
                      <ManageTasks
                        state={taskState}
                        onShowTask={handleShowTask}
                        onTaskTypeUpdate={handleUpdateTaskType}
                        isNewTask={isNewTask}
                        label=''
                      />

                      {taskState[0].length > 0 && (
                        <Controller
                          name='enforceOrder'
                          control={control}
                          render={({ field }) => (
                            <Switch
                              label='Enforce task order'
                              tooltip='Each task will need to be completed before the next task can be started.'
                              checked={field.value}
                              onChange={(value) => field.onChange(value)}
                            />
                          )}
                        />
                      )}
                    </div>
                  </Accordion.PanelSection>
                  {(() => {
                    const notes = watch('notes');
                    const hasName = !!watch('name');
                    // Check if notes has actual content by examining the blocks for text
                    const hasNotes =
                      notes?.blocks?.some(
                        (block) => block.text && block.text.trim().length > 0
                      ) ?? false;
                    const hasTags = (watch('tags') || []).length > 0;
                    const hasAttributes =
                      (watch('includeAttributes') || []).length > 0;
                    const hasJobAttributes =
                      (watch('jobAttributes') || []).length > 0;
                    const hasAttachments =
                      (watch('attachments') || []).length > 0;
                    const hasTasks =
                      taskState[0].filter((task) => !task._destroy).length > 0;

                    const hasContent =
                      hasName ||
                      hasNotes ||
                      hasTags ||
                      hasAttributes ||
                      hasJobAttributes ||
                      hasAttachments ||
                      hasTasks;

                    const showButton =
                      (!selectedTemplate && hasContent) ||
                      (selectedTemplate &&
                        isTemplateModified(selectedTemplate));

                    return showButton ? (
                      <CreateTemplateButton
                        jobName={watch('name')}
                        tasks={taskState[0]}
                        notes={notes}
                        tags={watch('tags')}
                        includeAttributes={watch('includeAttributes')}
                        jobAttributes={watch('jobAttributes')}
                        attachments={watch('attachments')}
                        guestyCleaningStatus={watch('guestyCleaningStatus')}
                        onSuccess={(templateId, templateName) => {
                          toast.success(
                            `Template "${templateName}" is now available in your templates list`
                          );
                        }}
                      />
                    ) : null;
                  })()}
                </Accordion.Panel>
              </Accordion.GreyCard>
            </Accordion>

            {/* <FindAddContact
        label={t('job:form.customer')}
        type='Customer'
        // value={form.customer ? [form.customer] : []}
        value={form.customer ? [form.customer] : []}
        onChange={(value) => {
          setValue('customer', value.length ? value[0] : null);
        }}
      /> */}
            {/* GF-2761 temporarily hidden */}
            {/* <RadioGroup
        name='scope'
        title={t('job:form.scopeTitle')}
        options={Object.values(CreateJobScope).map((value) => ({
          value,
          label: t('type.' + value),
        }))}
        value={form.scope}
        onChange={(value) => {
          setValue('scopeIds', []);
          setValue('scope', value);
        }}
      /> */}

            {/* {form.scope === CreateJobScope.Asset && (
        <Select
          label={t('form.asset')}
          options={options.Asset}
          value={form.scopeIds.map((id) => id.toString())}
          onChange={(value) =>
            setValue(
              'scopeIds',
              value.map((v) => parseInt(v))
            )
          }
          multiple
        />
      )} */}
          </SideLayout.Body>
          {!isPopup && (
            <SideLayout.Foot
              className={classNames('rounded p-4', {
                '!fixed': isMobile,
              })}
            >
              <SaveJobActions
                assignee={watch('assignee')}
                jobName={watch('name')}
                loading={isSubmitting}
                onClick={submit}
                notifyDefault={notifyDefault}
                isMultipleJobCreation={isMultipleJobCreation}
                isAssetJob={!!initialAsset}
              />
            </SideLayout.Foot>
          )}
        </SideLayout>
        {isPopup && (
          <SideLayout.Foot
            className={classNames('rounded p-4', {
              '!fixed': isMobile,
            })}
          >
            <SaveJobActions
              assignee={watch('assignee')}
              jobName={watch('name')}
              loading={isSubmitting}
              onClick={submit}
              notifyDefault={notifyDefault}
              isMultipleJobCreation={isMultipleJobCreation}
              isAssetJob={!!initialAsset}
            />
          </SideLayout.Foot>
        )}
        {taskSection}
      </div>
      <Confirm
        title='Remove Template?'
        body='This template has been modified. Are you sure you want to remove it? Your changes will be lost.'
        confirm='Remove'
        cancel='Cancel'
        danger
        show={showRemoveConfirmation}
        onConfirm={() => {
          removeTemplate();
          setShowRemoveConfirmation(false);
          // Also reset asset-driven state when removing template
          setAssetDrivenState({
            isAssetDriven: false,
            selectedAssetIds: [],
          });
        }}
        onCancel={() => setShowRemoveConfirmation(false)}
      />

      {/* Template Switch Warning */}
      <TemplateSwitchWarning
        show={showTemplateSwitchWarning}
        pendingTemplate={pendingTemplate}
        currentTemplate={selectedTemplate}
        hasExistingTasks={taskState[0].filter((t) => !t._destroy).length > 0}
        hasSelectedAssets={assetDrivenState.selectedAssetIds.length > 0}
        selectedAssetCount={assetDrivenState.selectedAssetIds.length}
        existingTaskCount={taskState[0].filter((t) => !t._destroy).length}
        onConfirm={handleConfirmTemplateSwitch}
        onCancel={handleCancelTemplateSwitch}
      />
    </FormProvider>
  );
};

export function useEditTasks() {
  const [showTask, setShowTask] = useState<string | null>(null);
  const taskState = useState<TaskInput[]>([]);

  const updateTask = useCallback(
    (data: TaskInput) => {
      const [, setTasks] = taskState;
      setTasks((tasks) =>
        splice(
          tasks,
          tasks.findIndex(({ id }) => id === data.id),
          1,
          data
        )
      );

      setShowTask(null);
    },
    [taskState]
  );

  const deleteTask = useCallback(
    (id: string) => {
      const [, setTasks] = taskState;

      setTasks((tasks) => {
        const idx = tasks.findIndex((t) => t.id === id);
        return idx > -1 ? remove(tasks, idx) : tasks;
      });

      setShowTask(null);
    },
    [taskState]
  );

  return {
    taskState,
    showTask,
    setShowTask,
    updateTask,
    deleteTask,
  };
}

// Animation wrapper component
function AnimationWrapper({
  animationSource = 'default',
  children,
}: {
  animationSource?: 'asset' | 'job' | 'default';
  children: React.ReactNode;
}) {
  if (animationSource === 'asset') {
    // Fade-in animation for asset-triggered job creation
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: [0.4, 0.0, 0.2, 1], // Material Design easing
        }}
        className='h-full'
      >
        {children}
      </motion.div>
    );
  }

  if (animationSource === 'job') {
    // Future: expanding from right animation for job-to-job creation
    // For now, return without animation - will implement later
    return <>{children}</>;
  }

  // Default: no animation
  return <>{children}</>;
}
